import { useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { can } from "../../../../shared/auth/access";
import type { SessionUser, Permission } from "../../../../shared/auth/access";
import type { Rfq, RfqOffers, RfqCommunications, UnifiedEvaluation, ContractCompliance } from "../../domain/rfq-types";
import { extendRfqDeadline, unsealRfq, evaluateRfqOffers, evaluateRfqContract, assessRfqContractOrder, publishRfqAnnouncement, answerRfqQuestion } from "../../infrastructure/rfqs/rfq-governance-gateway";
import { localDateTimeToUtc } from "../../rfq-timezone";
import type { ProcurementScope } from "../shared/useProcurementScope";
import { useRfqReadScope } from "./useRfqReadScope";
import { useRfqGovernanceDrafts } from "./useRfqGovernanceDrafts";
import { rfqUiMessage } from "./rfq-presentation";

type Props = {
  scope: ProcurementScope; user: SessionUser; rfq: Rfq | null; offers: RfqOffers | null; communications: RfqCommunications;
  setRfq: Dispatch<SetStateAction<Rfq | null>>; setEvaluation: Dispatch<SetStateAction<UnifiedEvaluation | null>>;
  setContractCompliance: Dispatch<SetStateAction<ContractCompliance[]>>; setGovernanceError: Dispatch<SetStateAction<string>>;
  loadGovernance: (rfqId: string, afterWrite?: boolean) => Promise<boolean>;
};
export function useRfqGovernanceCommands({ scope, user, rfq, offers, communications, setRfq, setEvaluation, setContractCompliance, setGovernanceError, loadGovernance }: Props) {
  const readScope = useRfqReadScope(scope, rfq?.id);
  const drafts = useRfqGovernanceDrafts(readScope);
  const latest = useRef({ rfq, offers, communications });
  latest.current = { rfq, offers, communications };
  const lock = useRef<{ scope: typeof readScope; token: object } | null>(null);
  const [pending, setPending] = useState<typeof readScope | null>(null);
  const isSnapshotCurrent = () => readScope.isCurrent() && latest.current.rfq?.id === rfq?.id && latest.current.rfq?.status === rfq?.status && (latest.current.rfq?.current_version ?? latest.current.rfq?.currentVersion) === (rfq?.current_version ?? rfq?.currentVersion);
  const run = async (permission: Permission, work: () => Promise<string>) => {
    if (!rfq || !isSnapshotCurrent() || !can(user, permission) || lock.current?.scope === readScope) return;
    const token = {};
    lock.current = { scope: readScope, token };
    setPending(readScope);
    setGovernanceError("");
    try {
      const message = await work();
      if (!readScope.isCurrent()) return;
      let refreshed = false;
      try { refreshed = await loadGovernance(rfq.id, true); } catch { /* A confirmed write remains complete when refresh fails. */ }
      if (!readScope.isCurrent()) return;
      if (!refreshed) {
        const warning = "İşlem tamamlandı, ancak ekranın güncelliği doğrulanamadı. Bilgileri yenileyin; işlemi tekrar göndermeyin.";
        setGovernanceError(warning);
        scope.notify(`${message} ${warning}`);
      } else scope.notify(message);
    } catch (value) {
      if (!readScope.isCurrent()) return;
      const code = value instanceof Error && "code" in value && typeof value.code === "string" ? value.code : "";
      setGovernanceError(["NETWORK_ERROR", "INVALID_API_RESPONSE"].includes(code) || /^HTTP_5/.test(code)
        ? "İşlemin sonucu doğrulanamadı. Tekrar göndermeden önce güncel bilgileri yenileyip durumunu kontrol edin."
        : rfqUiMessage(value, "İşlem güvenli biçimde tamamlanamadı."));
    } finally {
      if (lock.current?.token === token) {
        lock.current = null;
        if (readScope.isCurrent()) setPending(null);
      }
    }
  };
  const updateStatus = (status: string) => {
    if (!isSnapshotCurrent() || !rfq) return;
    setRfq(current => current?.id === rfq.id && current.status === rfq.status && (current.current_version ?? current.currentVersion) === (rfq.current_version ?? rfq.currentVersion) ? { ...current, status } : current);
  };
  const assertDraftCurrent = (key: keyof typeof drafts.renderedRevisions) => {
    if (drafts.revisions.current[key] !== drafts.renderedRevisions[key]) throw new Error("Taslak değişti. Güncel metni kontrol edip yeniden deneyin.");
  };
  const extendDeadline = () => run("rfq.deadline.override", async () => {
    if (!rfq) return "";
    assertDraftCurrent("deadlineExtension");
    if (drafts.deadlineExtension.reason.trim().length < 10) throw new Error("Süre uzatma gerekçesi en az 10 karakter olmalıdır.");
    const revision = drafts.revisions.current.deadlineExtension;
    const status = await extendRfqDeadline(rfq, localDateTimeToUtc(drafts.deadlineExtension.deadlineAt, rfq.timezone), drafts.deadlineExtension.reason);
    if (readScope.isCurrent()) { updateStatus(status); drafts.clearIfUnchanged("deadlineExtension", revision); }
    return "Teklif son tarihi gerekçesiyle uzatıldı.";
  });
  const unseal = () => run("rfq.sealed.open", async () => {
    if (!rfq) return "";
    assertDraftCurrent("unsealReason");
    if (drafts.unsealReason.trim().length < 10) throw new Error("Açma gerekçesi en az 10 karakter olmalıdır.");
    const revision = drafts.revisions.current.unsealReason;
    await unsealRfq(rfq, drafts.unsealReason);
    if (readScope.isCurrent()) { updateStatus("UNSEALED"); drafts.clearIfUnchanged("unsealReason", revision); }
    return "Kapalı teklifler sunucu zamanı doğrulanarak açıldı.";
  });
  const evaluateUnifiedOffers = () => run("offer.manage", async () => {
    if (!rfq) return "";
    if (rfq.method === "SEALED" && !latest.current.offers?.unsealed) throw new Error("Kapalı teklifler açılmadan değerlendirme yapılamaz.");
    const result = await evaluateRfqOffers(rfq.id);
    if (readScope.isCurrent()) setEvaluation(current => current && current.version > result.version ? current : result);
    return "Manuel ve portal teklifleri aynı kurallarla değerlendirildi.";
  });
  const checkOffer = (offerVersionId: string) => {
    if (!latest.current.offers?.unsealed || !latest.current.offers.offers.some(offer => offer.id === offerVersionId)) throw new Error("Teklif listesi değişti. Güncel teklifi seçin.");
  };
  const evaluateContract = (offerVersionId: string) => run("contract.compliance.evaluate", async () => {
    if (!rfq) return "";
    checkOffer(offerVersionId);
    const result = await evaluateRfqContract(rfq.id, offerVersionId);
    if (readScope.isCurrent()) setContractCompliance(current => [...current.filter(item => item.offerVersionId !== offerVersionId), result]);
    return "Sözleşme uygunluğu sunucu koşullarıyla hesaplandı.";
  });
  const createContractOrder = (offerVersionId: string) => run("contract.order.authorize", async () => {
    if (!rfq) return "";
    checkOffer(offerVersionId);
    const result = await assessRfqContractOrder(rfq.id, offerVersionId);
    return result.created ? `${result.orderNo} numaralı sipariş sözleşme yetkisiyle oluşturuldu.` : result.reason;
  });
  const publishAnnouncement = () => run("supplier_portal.question.manage", async () => {
    if (!rfq) return "";
    assertDraftCurrent("announcementBody");
    if (drafts.announcementBody.trim().length < 2) throw new Error("Duyuru en az 2 karakter olmalıdır.");
    const revision = drafts.revisions.current.announcementBody;
    await publishRfqAnnouncement(rfq.id, drafts.announcementBody);
    drafts.clearIfUnchanged("announcementBody", revision);
    return "Genel duyuru tüm davetliler için yayımlandı.";
  });
  const answerQuestion = (questionId: string) => run("supplier_portal.question.manage", async () => {
    if (!rfq) return "";
    assertDraftCurrent("answerDrafts");
    const draft = drafts.answerDrafts[questionId];
    const question = latest.current.communications.questions.find(item => item.id === questionId);
    if (!question || question.answer || !draft || draft.body.trim().length < 2 || !["PRIVATE", "ALL_INVITEES"].includes(draft.visibility)) throw new Error("Yanıt bekleyen soruya en az 2 karakterlik bir yanıt girin.");
    await answerRfqQuestion(rfq.id, questionId, draft);
    drafts.clearAnswerIfUnchanged(questionId, draft);
    return draft.visibility === "PRIVATE" ? "Yanıt yalnız soru sahibine iletildi." : "Yanıt tedarikçi kimliği açıklanmadan tüm davetlilere duyuruldu.";
  });
  return { ...drafts, governanceBusy: pending === readScope, extendDeadline, unseal, evaluateUnifiedOffers, evaluateContract, createContractOrder, publishAnnouncement, answerQuestion };
}
